import { matchupStatus, round1 } from './model.js';
/** The week the scoreboard should open to: the live week, else the latest one. */
export function scoreboardWeek(data) {
    const weeks = [...new Set(data.matchups.map((m) => m.week))];
    if (weeks.length === 0)
        return null;
    if (data.liveWeeks.length > 0)
        return Math.max(...data.liveWeeks);
    // No live week: show the most recent completed week if any, else the earliest
    // scheduled week (the upcoming slate).
    if (data.completedWeeks.length > 0)
        return Math.max(...data.completedWeeks);
    return Math.min(...weeks);
}
export function weekScoreboard(data, money, week) {
    const record = new Map(data.teams.map((t) => [t.ownerId, { wins: t.wins, losses: t.losses, ties: t.ties }]));
    const side = (ownerId, score) => ({
        ownerId,
        score,
        ...(record.get(ownerId) ?? { wins: 0, losses: 0, ties: 0 }),
    });
    const games = data.matchups
        .filter((m) => m.week === week)
        .map((m) => {
        const status = matchupStatus(m);
        const leaderId = m.homeScore === m.awayScore || status === 'scheduled'
            ? null
            : m.homeScore > m.awayScore
                ? m.homeOwnerId
                : m.awayOwnerId;
        return {
            week,
            status,
            home: side(m.homeOwnerId, m.homeScore),
            away: side(m.awayOwnerId, m.awayScore),
            leaderId,
            margin: Math.round(Math.abs(m.homeScore - m.awayScore) * 10) / 10,
            stakes: [],
        };
    });
    annotateStakes(data, money, week, games);
    return games;
}
export function weekStakes(data, money, week) {
    const inWeek = data.results.filter((r) => r.week === week);
    if (inWeek.length === 0)
        return [];
    const statuses = data.matchups
        .filter((m) => m.week === week)
        .map((m) => matchupStatus(m));
    const anyFinal = statuses.includes('final');
    const anyLive = statuses.includes('live');
    // Every game called. A single scheduled or live matchup keeps the week open,
    // which is what stops a stake reading as banked while it is still winnable.
    const settled = statuses.length > 0 && statuses.every((s) => s === 'final');
    if (!anyFinal && !anyLive)
        return [];
    const stakes = [];
    // --- The weekly pots: whoever is winning each one right now. ---
    //
    // Every weekly award, decided by its own computation rather than by assuming
    // the pot goes to the highest score: a league can pay the lowest, or the most
    // points against, or two of these at once, and the money board resolves all
    // of those correctly — the scoreboard has no business disagreeing with it.
    //
    // They only pay during the regular season, so playoff weeks carry no such
    // stake even though someone is still the top scorer.
    if (week <= money.config.regularSeasonWeeks) {
        for (const rule of money.config.awards) {
            if (rule.compute?.kind !== 'weeklyExtreme')
                continue;
            const { metric, pick } = rule.compute;
            const valueOf = (r) => metric === 'pointsFor' ? r.score : r.opponentScore;
            const values = inWeek.map(valueOf);
            // Nothing has been posted yet, so no pot has a meaningful leader. The
            // test is whether ANYBODY has scored, not whether the leader has: a pot
            // for the LOWEST score is very often led by a zero — or by a negative,
            // which a defense can genuinely post — and the money board settles and
            // pays exactly that. Guarding on the leader's own value hid the stake
            // precisely when it was most worth showing.
            if (!values.some((v) => v > 0))
                continue;
            const target = pick === 'high' ? Math.max(...values) : Math.min(...values);
            // Ties are real money here, so every tied manager is named.
            const ownerIds = inWeek
                .filter((r) => valueOf(r) === target)
                .map((r) => r.ownerId);
            if (ownerIds.length === 0)
                continue;
            stakes.push({
                kind: 'weekly',
                ownerIds,
                label: rule.label,
                amount: rule.amount,
                settled,
                note: settled
                    ? `Banked the $${rule.amount} ${rule.label}`
                    : `Leading the $${rule.amount} ${rule.label}`,
                detail: `${round1(target)} pts`,
            });
        }
    }
    // --- Survivor: who went out this week, or who is on the bubble. ---
    //
    // `survivor.alive` is the pool as it stands AFTER every completed week, so it
    // cannot describe a week that has already been settled. By then the manager
    // this week eliminated is gone from it, and the lowest of whoever is left is
    // the NEXT player at risk — so a settled week named its own second-lowest
    // scorer and called them eliminated. Week 1 read as the wrong manager for
    // exactly that reason.
    //
    // A settled week has a recorded answer, so use it: it handles a tie that took
    // two managers out, and it cannot disagree with the money board. A week still
    // being played has no answer yet, so reconstruct the pool as it stood at
    // kickoff — everyone except those eliminated in EARLIER weeks — and name its
    // low scorer as the bubble.
    //
    // Scoped the way computeSurvivor scopes itself: a week the pool doesn't play
    // (a playoff week, or anything past the week it resolves) carries no stake.
    const survivorWeek = week <= money.survivor.finalWeek && week <= money.config.regularSeasonWeeks;
    const eliminatedBefore = new Set(money.survivor.eliminations.filter((e) => e.week < week).map((e) => e.ownerId));
    const poolAtKickoff = data.owners
        .map((o) => o.id)
        .filter((id) => !eliminatedBefore.has(id));
    if (survivorWeek && poolAtKickoff.length > 1) {
        const decided = money.survivor.eliminations.filter((e) => e.week === week);
        const inPool = inWeek.filter((r) => !eliminatedBefore.has(r.ownerId));
        const low = inPool.length ? Math.min(...inPool.map((r) => r.score)) : null;
        const marked = decided.length
            ? decided.map((e) => e.ownerId)
            : low === null
                ? []
                : inPool.filter((r) => r.score === low).map((r) => r.ownerId);
        if (marked.length > 0) {
            // The pool is computed for every league; only one that pays for it has a
            // rule to take the name and the dollars from.
            const rule = money.config.awards.find((a) => a.compute?.kind === 'survivor');
            const scoreOf = (id) => decided.find((e) => e.ownerId === id)?.score ??
                inPool.find((r) => r.ownerId === id)?.score;
            const scores = marked.map(scoreOf).filter((s) => s != null);
            stakes.push({
                kind: 'survivor',
                ownerIds: marked,
                label: rule?.label ?? 'Survivor',
                amount: rule?.amount ?? null,
                settled: decided.length > 0,
                note: decided.length
                    ? 'Eliminated from Survivor'
                    : 'On the Survivor bubble',
                detail: scores.length ? `${round1(Math.min(...scores))} pts` : undefined,
            });
        }
    }
    return stakes;
}
/** Attach the money angle to the games that have it. */
function annotateStakes(data, money, week, games) {
    const gameFor = (ownerId) => games.find((g) => g.home.ownerId === ownerId || g.away.ownerId === ownerId);
    for (const stake of weekStakes(data, money, week)) {
        for (const ownerId of stake.ownerIds) {
            const g = gameFor(ownerId);
            // Two tied managers can share a card; the note belongs on it once.
            if (g && !g.stakes.includes(stake.note))
                g.stakes.push(stake.note);
        }
    }
}
//# sourceMappingURL=scoreboard.js.map